/** Decorative scene for the sign-in page: leaves at the top, a school with three children walking to it */

function Leaf({ x, y, r, c }: { x: number; y: number; r: number; c: string }) {
  return <ellipse cx={x} cy={y} rx={r} ry={r * 0.55} fill={c} transform={`rotate(${(x * 7 + y * 3) % 70 - 35} ${x} ${y})`} />;
}

function Branches() {
  const leaves: [number, number, number][] = [];
  // Leaves along two branches, coloured from deep green to spring yellow
  for (let i = 0; i < 26; i++) leaves.push([20 + i * 11 + (i % 3) * 6, 18 + i * 6 + ((i * 37) % 23), 13 + (i % 4) * 2]);
  for (let i = 0; i < 18; i++) leaves.push([10 + (i % 4) * 22, 120 + i * 10 + ((i * 29) % 17), 12 + (i % 3) * 3]);
  const colors = ['#7FB547', '#A6CC4A', '#C7DD5A', '#5E9E3A', '#93C24B'];
  return (
    <svg className="ls-branches" viewBox="0 0 320 340" aria-hidden="true">
      <path d="M-10 40 C 70 60, 150 90, 300 150" stroke="#6B4A2E" strokeWidth="7" fill="none" strokeLinecap="round" />
      <path d="M-10 120 C 30 160, 60 220, 80 320" stroke="#6B4A2E" strokeWidth="6" fill="none" strokeLinecap="round" />
      <path d="M110 75 C 130 40, 160 25, 200 18" stroke="#6B4A2E" strokeWidth="4" fill="none" strokeLinecap="round" />
      {leaves.map(([x, y, r], i) => (
        <Leaf key={i} x={x} y={y} r={r} c={colors[i % colors.length]} />
      ))}
    </svg>
  );
}

/** A child seen from behind, walking, with a backpack */
function Kid({ x, y, bag, shirt, pants, hair, girl, scale = 1 }: { x: number; y: number; bag: string; shirt: string; pants: string; hair: string; girl?: boolean; scale?: number }) {
  // Drawn with the feet at y≈268; placed so the feet stand on the path at y
  return (
    <g transform={`translate(${x} ${y - 268 * scale}) scale(${scale})`}>
      <ellipse cx="0" cy="268" rx="28" ry="6" fill="rgb(27 42 74 / 0.12)" />
      {/* legs */}
      <rect x="-12" y="196" width="10" height="64" rx="5" fill={pants} transform="rotate(6 -7 196)" />
      <rect x="3" y="196" width="10" height="64" rx="5" fill={pants} transform="rotate(-8 8 196)" />
      <ellipse cx="-12" cy="263" rx="9" ry="5" fill="#7A4A2A" />
      <ellipse cx="14" cy="261" rx="9" ry="5" fill="#7A4A2A" />
      {girl && <path d="M-22 186 L22 186 L28 214 L-28 214 Z" fill={pants} />}
      {/* body and arms */}
      <rect x="-21" y="122" width="42" height="72" rx="16" fill={shirt} />
      <rect x="-30" y="130" width="11" height="52" rx="5.5" fill={shirt} transform="rotate(10 -24 130)" />
      <rect x="19" y="130" width="11" height="52" rx="5.5" fill={shirt} transform="rotate(-10 24 130)" />
      {/* head from behind */}
      <rect x="-6" y="112" width="12" height="14" fill="#E8B48F" />
      <circle cx="0" cy="96" r="22" fill={hair} />
      {girl && (
        <>
          <circle cx="-22" cy="98" r="8" fill={hair} />
          <circle cx="22" cy="98" r="8" fill={hair} />
          <circle cx="-17" cy="86" r="4" fill="#F2A900" />
          <circle cx="17" cy="86" r="4" fill="#F2A900" />
        </>
      )}
      {/* backpack */}
      <rect x="-24" y="130" width="48" height="60" rx="14" fill={bag} />
      <rect x="-16" y="160" width="32" height="22" rx="7" fill="rgb(255 255 255 / 0.22)" />
      <rect x="-18" y="124" width="36" height="12" rx="6" fill={bag} />
    </g>
  );
}

function School() {
  return (
    <svg className="ls-school" viewBox="0 0 520 470" aria-hidden="true">
      {/* trees behind */}
      <circle cx="60" cy="120" r="70" fill="#9CC86A" />
      <circle cx="110" cy="80" r="55" fill="#B4D776" />
      <rect x="70" y="140" width="12" height="140" fill="#7A5A3A" />
      <circle cx="470" cy="170" r="55" fill="#A6CF72" />
      <rect x="465" y="200" width="10" height="80" fill="#7A5A3A" />
      {/* building */}
      <rect x="120" y="150" width="340" height="140" fill="#F3D9A4" />
      <rect x="120" y="150" width="340" height="14" fill="#B9C8DE" />
      <polygon points="230,150 290,100 350,150" fill="#F3D9A4" />
      <polygon points="222,152 290,94 358,152" fill="none" stroke="#B9C8DE" strokeWidth="10" strokeLinejoin="round" />
      <circle cx="290" cy="130" r="14" fill="#fff" stroke="#9AA9C0" strokeWidth="3" />
      <path d="M290 130v-8M290 130l6 4" stroke="#55627A" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="290" y1="94" x2="290" y2="40" stroke="#8C99AE" strokeWidth="3" />
      <path d="M290 42 h28 l-6 8 6 8 h-28z" fill="#F08A5D" />
      <rect x="262" y="210" width="56" height="80" fill="#8FB3D9" />
      <path d="M290 210v80" stroke="#fff" strokeWidth="3" />
      {[140, 180, 360, 400].map((x) =>
        [180, 230].map((y) => (
          <g key={`${x}${y}`}>
            <rect x={x} y={y} width="30" height="30" fill="#A9C6E6" />
            <path d={`M${x + 15} ${y}v30M${x} ${y + 15}h30`} stroke="#fff" strokeWidth="2" />
          </g>
        )),
      )}
      <rect x="250" y="288" width="80" height="8" fill="#E5DDCD" />
      {/* lawn and path */}
      <path d="M0 290 C 160 282, 360 282, 520 292 V470 H0z" fill="#CFE4B0" />
      <path d="M262 296 C 250 360, 200 420, 120 470 H 420 C 360 420, 320 360, 318 296 Z" fill="#F1EADB" />
      {/* the children */}
      <Kid x={205} y={452} bag="#3E6FB0" shirt="#F4F6FA" pants="#2F3A55" hair="#3B2416" scale={0.82} />
      <Kid x={285} y={440} bag="#E68AA4" shirt="#E9967A" pants="#3D5C9A" hair="#5A3A22" girl scale={0.76} />
      <Kid x={360} y={448} bag="#F2B92E" shirt="#3D5C9A" pants="#2F4C82" hair="#3B2416" scale={0.78} />
    </svg>
  );
}

export function LoginScene() {
  return (
    <div className="ls" aria-hidden="true">
      <Branches />
      <School />
      <svg className="ls-waves" viewBox="0 0 1440 300" preserveAspectRatio="none">
        <path d="M0 220 C 300 180, 520 260, 820 210 S 1250 120, 1440 150 V300 H0z" fill="rgb(255 255 255 / 0.5)" />
        <path d="M700 300 C 900 220, 1150 200, 1440 210 V300z" fill="rgb(247 224 170 / 0.55)" />
      </svg>
    </div>
  );
}
